import { mpEventInfo } from '@/constant'
import type { MakeLessonMaterialFilterT } from '@/contentComponents/makeLessonMaterial/(filter)/service/MakeLessonMaterialFilter.service'
import type { DerivationWorksheetByStudentConceptChip } from '@/service/derivation/(filter)/(by)/derivationWorksheetByStudentConceptChip.type'
import type { DerivationWorksheetByWrongWorksheet } from '@/service/derivation/(filter)/(by)/derivationWorksheetByWrongWorksheet.type'
import type { DefaultResponse } from '@/types/common'
import maxios, { type CustomRequestConfigMeta } from '@/utils/axios/maxios'

/***
 * @link https://freewheelin.postman.co/collections/5542627-31bd5fa5-3069-4f49-8c6d-97a718edb97e?version=latest&workspace=ef6c4fe1-201a-4de6-8b0c-2a7de439d3ff#6983cca7-f421-41ed-b1de-e24cc5d98251
 ***/

// n차 교재, 학습지 API
const derivationApi = {
  // 교재 문항별 오답관리
  getByWorkbookProblem: (params: req.DerivationByWorkbookProblem) => {
    return maxios.get<DefaultResponse<res.WorkbookDerivation>>('/derivation/workbook/problem', {
      params: {
        ...params,
        mp_event_name: mpEventInfo['교재문제_쌍둥이유사검색&문제별오답학습지_문제 조회'],
      },
    })
  },
  // 교재 페이지별 오답관리
  getByWorkbookPage: (params: req.DerivationByWorkbookPage) => {
    return maxios.get<DefaultResponse<res.WorkbookDerivation>>('/derivation/workbook/page', {
      params: {
        ...params,
        mp_event_name: mpEventInfo.교재페이지별오답학습지문제조회,
      },
    })
  },

  getRefreshedWorkbookProblem: (params: req.MakeWorkbookRefreshedProblemParams) => {
    return maxios.get<DefaultResponse<res.MakeWorkbookRefreshedProblem>>('/derivation/problem', {
      params: {
        bookType: params.bookType,
        filterId: params.filterId,
        conceptIds: params.conceptIds,
        excludedProblemIds: params.excludedProblemIds,
        mp_event_name: mpEventInfo.교재생성2단계새_문제추가,
      },
    })
  },
  postRefreshedWorkbookProblem: (payload: req.MakeWorkbookRefreshedProblemParams) => {
    return maxios.post<res.MakeWorkbookRefreshedProblem>(
      '/derivation/problem',
      {
        bookType: payload.bookType,
        filterId: payload.filterId,
        conceptIds: payload.conceptIds,
        excludedProblemIds: payload.excludedProblemIds,
      },
      {
        params: {
          mp_event_name: mpEventInfo.교재생성2단계새_문제추가,
        },
      },
    )
  },

  // 쌍둥이&유사문제 문제조회 : 백엔드 도메인따라가기 위해 derivation에 두었지만.. 원래는 problem도메인이고, 추후 바뀔 예정이라함..
  /**
   * @deprecated
   * 리뉴얼된 교재에서는 절대 쓰지 말것
   */
  async _postPairSimilarProblemList(
    id: res.Problem<'WORKSHEET' | 'WORKBOOK'>['id'],
    payload: req.PairSimilarProblem,
  ) {
    return maxios.post<res.PairSimilarProblem>(`/derivation/problem/${id}`, payload, {
      params: {
        mp_event_name: mpEventInfo.학습지만들기2_쌍둥이유사조회,
      },
    })
  },
  postPairSimilarProblemList(
    id: res.Problem<'WORKSHEET' | 'WORKBOOK'>['id'],
    payload: Omit<req.PairSimilarProblem, 'filterId'> & { filter: MakeLessonMaterialFilterT },
  ) {
    return maxios.post<res.PairSimilarProblem>(`/derivation/problem/${id}`, payload, {
      params: {
        mp_event_name: mpEventInfo.학습지만들기2_쌍둥이유사조회,
      },
    })
  },
  wrongWorksheet: {
    filterResponse: {
      getByWeakWorksheetFilter: async (
        filter:
          | DerivationWorksheetByWrongWorksheet.ByWeakWorksheetFilter.ByConcept
          | DerivationWorksheetByWrongWorksheet.ByWeakWorksheetFilter.ByProblem,
        meta?: CustomRequestConfigMeta,
      ) => {
        return maxios.get<DefaultResponse<res.WorksheetDerivation>>(
          '/derivation/worksheet/problem',
          {
            params: {
              ...filter,
              mp_event_name: mpEventInfo.학습지오답학습지문제조회,
            },
            meta,
          },
        )
      },
      // 자동시험 오답학습지 — 학습지 derivation 과 동일 응답 형태(`/derivation/worksheet/problem`),
      // `studentWorksheetIdList → assignedCasIdList` 미러링.
      getByCasProblem: async (
        filter: DerivationWorksheetByWrongWorksheet.ByCasFilter,
        meta?: CustomRequestConfigMeta,
      ) => {
        return maxios.get<DefaultResponse<res.WorksheetDerivation>>(
          '/derivation/cas/problem',
          {
            params: {
              ...filter,
            },
            meta,
          },
        )
      },
    },
  },
  achievement: {
    filterResponse: {
      byStudent: async (
        params:
          | DerivationWorksheetByStudentConceptChip.Filter.ByConcept
          | DerivationWorksheetByStudentConceptChip.Filter.ByProblem,
      ) => {
        return maxios.get<DefaultResponse<res.WorksheetDerivation>>(
          `/derivation/weak-concept-chip`,
          {
            params: {
              ...params,
              mp_event_name: mpEventInfo.유형분석_학습지만들기,
            },
          },
        )
      },
    },
  },
  // 학습지 단원 유형별 오답관리
  getByWorksheetChapter: <
    T extends res.WrongWorksheetType,
    U = T extends 'WRONG_CONCEPT_WORKSHEET' ? '유형수' | '정답률' : never,
  >(
    params: req.ByWorksheetChapter<T, U>,
  ) => {
    return maxios.get<DefaultResponse<res.WorksheetDerivation>>('/derivation/weak-concept', {
      params: {
        ...params,
        mp_event_name: mpEventInfo.단원별취약유형학습지문제조회,
      },
    })
  },
  // 학습지 기간별 오답관리
  getByWorksheetDateRange: (params: req.ByWorksheetDateRange) => {
    return maxios.get<DefaultResponse<res.WorksheetDerivation>>('/derivation/date-range', {
      params: {
        ...params,
        mp_event_name: mpEventInfo.기간별오답학습지문제조회,
      },
    })
  },

  // 취약유형칩 오답관리
  getByWeakConceptChip: (params: req.ByWeakConceptChip) => {
    return maxios.get<DefaultResponse<res.WorksheetDerivation>>(`/derivation/weak-concept-chip`, {
      params: {
        ...params,
        mp_event_name: mpEventInfo.유형분석_학습지만들기,
      },
    })
  },

  getByOneClick: (params: req.ByOneClick) => {
    return maxios.get<DefaultResponse<res.WorksheetDerivation>>('/derivation/one-click', {
      params: {
        ...params,
        mp_event_name: mpEventInfo.학습지만들기_원클릭복습학습지_문제조회,
      },
    })
  },
}

export default derivationApi
